import { useMemo, useRef, useState, type MouseEvent } from 'react';
import type { CellValueChangedEvent, ColDef, ICellRendererParams } from 'ag-grid-community';
import { NJIconButton, NJTooltip } from '@engie-group/fluid-design-system-react';
import DataTable from '@shared/DataTable';

/** Nemo `AddButtonDivider`: 2px brand bar with a centred round « + » button. */
export function AddButtonDivider({ onClick, label = 'Ajouter une ligne', testId }: { onClick: () => void; label?: string; testId?: string }) {
    return (
        <div className="production-contracts-add-divider">
            <span className="production-contracts-add-divider-bar" />
            <button type="button" className="production-contracts-add-divider-button" onClick={onClick} aria-label={label} data-testid={testId}>
                +
            </button>
        </div>
    );
}

/** Nemo row delete action (tertiary `delete` icon in a « Supprimer » tooltip). */
export function DeleteRowButton({ onClick }: { onClick: () => void }) {
    return (
        <div className="production-contracts-actions">
            <NJTooltip text="Supprimer" hasArrow placement="top">
                <NJIconButton icon="delete_outline" label="Supprimer" variant="tertiary" onClick={onClick} />
            </NJTooltip>
        </div>
    );
}

/**
 * Nemo `EditableRowTable`: an Actions column (80px, delete) is appended, a « + » divider appears
 * under the hovered row to insert a row after it, and a standalone divider is shown when empty.
 */
export function EditableRowTable<TRow extends object>({ rows, onChange, columns, createEmptyRow, testId }: {
    rows: TRow[];
    onChange: (rows: TRow[]) => void;
    columns: ColDef<TRow>[];
    createEmptyRow: () => TRow;
    testId: string;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [hovered, setHovered] = useState<{ index: number; top: number } | null>(null);

    const insertAfter = (index: number) => {
        const next = [...rows];
        next.splice(index + 1, 0, createEmptyRow());
        onChange(next);
    };

    const header = useMemo<ColDef<TRow>[]>(() => [
        ...columns,
        {
            headerName: 'Actions',
            width: 80,
            flex: 0,
            editable: false,
            sortable: false,
            resizable: false,
            headerClass: 'production-contracts-header-center',
            cellRenderer: ({ node }: ICellRendererParams<TRow>) => (
                <DeleteRowButton onClick={() => onChange(rows.filter((_, i) => i !== node.rowIndex))} />
            ),
        },
    ], [columns, rows, onChange]);

    const onMouseMove = (event: MouseEvent<HTMLDivElement>) => {
        const row = (event.target as HTMLElement).closest('.ag-row');
        const container = containerRef.current;
        const index = Number(row?.getAttribute('row-index'));
        if (!row || !container || Number.isNaN(index)) return;
        const top = row.getBoundingClientRect().bottom - container.getBoundingClientRect().top;
        setHovered((current) => (current?.index === index && current.top === top ? current : { index, top }));
    };

    return (
        <div ref={containerRef} className="production-contracts-editable-rows" onMouseMove={onMouseMove} onMouseLeave={() => setHovered(null)}>
            <DataTable<TRow>
                testId={testId}
                header={header}
                data={rows}
                defaultColDef={{ flex: 1, sortable: false, resizable: false, headerClass: 'production-contracts-header-center', cellClass: 'production-contracts-center' }}
                onCellValueChanged={(event: CellValueChangedEvent<TRow>) => {
                    const next = [...rows];
                    next[event.rowIndex!] = { ...event.data };
                    onChange(next);
                }}
            />
            {hovered && (
                <div className="production-contracts-hover-add" style={{ top: hovered.top - 12 }}>
                    <AddButtonDivider onClick={() => insertAfter(hovered.index)} testId={`add-row-after-${hovered.index}`} />
                </div>
            )}
            {rows.length === 0 && (
                <div className="production-contracts-add-first">
                    <AddButtonDivider onClick={() => insertAfter(-1)} testId={`${testId}-add-first-row`} />
                </div>
            )}
        </div>
    );
}
